import { ImageIcon } from 'lucide-react';

import type { IncidentLocationSchema } from '@/services/types/incident-types';

interface IncidentLocationSectionProps {
  location: IncidentLocationSchema | null | undefined;
  onClick?: () => void;
}

/**
 * Get the best available image URL from location data
 * Prefers satellite, then street view angles
 */
const getBestImageUrl = (
  location: IncidentLocationSchema | null | undefined,
): string | null => {
  if (!location) return null;

  // Prefer satellite image, then street view in order
  return (
    location.satellite_image_url ||
    location.streetview_image_url_0 ||
    location.streetview_image_url_90 ||
    location.streetview_image_url_180 ||
    location.streetview_image_url_270 ||
    null
  );
};

/**
 * Count available images for display
 */
const countAvailableImages = (
  location: IncidentLocationSchema | null | undefined,
): number => {
  if (!location) return 0;

  let count = 0;
  if (location.satellite_image_url) count += 1;
  if (location.streetview_image_url_0) count += 1;
  if (location.streetview_image_url_90) count += 1;
  if (location.streetview_image_url_180) count += 1;
  if (location.streetview_image_url_270) count += 1;
  return count;
};

/**
 * Faded cover image section for incident location
 * Displays a subtle background image that reveals on hover
 */
export const IncidentLocationSection = ({
  location,
  onClick,
}: IncidentLocationSectionProps) => {
  const imageUrl = getBestImageUrl(location);
  const imageCount = countAvailableImages(location);

  if (!imageUrl) return null;

  return (
    <div
      className="relative h-24 w-full cursor-pointer group overflow-hidden"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.();
        }
      }}
      aria-label="View location images"
    >
      {/* Background image with fade effect */}
      <div
        className="absolute inset-0 bg-cover bg-center transition-opacity duration-300 opacity-30 group-hover:opacity-60"
        style={{ backgroundImage: `url(${imageUrl})` }}
      />

      {/* Gradient overlay for better text readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/80" />

      {/* Hover indicator */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="bg-black/60 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5">
          <ImageIcon className="h-3.5 w-3.5" />
          View {imageCount} {imageCount === 1 ? 'image' : 'images'}
        </div>
      </div>

      {/* Bottom-right badge showing image count (visible when not hovered) */}
      <div className="absolute bottom-2 right-2 bg-black/40 text-white px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 group-hover:opacity-0 transition-opacity duration-200">
        <ImageIcon className="h-3 w-3" />
        {imageCount}
      </div>
    </div>
  );
};
